import { Outlet, useLocation, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import api from '../api/axios';

export default function Layout() {
  const location = useLocation();
  const { user, selectedBusiness, selectBusiness, availableBusinesses, logout } = useAuth();
  const [switchToast, setSwitchToast] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [loadingBiz, setLoadingBiz] = useState(true);
  const navigate = useNavigate();
  const [automationOpen, setAutomationOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [connectedPhone, setConnectedPhone] = useState(null);

  useEffect(() => {
    if (!selectedBusiness?.id) { setConnectedPhone(null); return; }
    api.get('/connect/profile', { params: { business_id: selectedBusiness.id } })
      .then(r => setConnectedPhone(r.data?.display_phone_number || null))
      .catch(() => setConnectedPhone(null));
  }, [selectedBusiness]);
  const [addAccError, setAddAccError] = useState('');
  const [addAccLoading, setAddAccLoading] = useState(false);
  const FB_APP_ID = '1430190582144691';

  useEffect(() => {
    if (!switchToast) return;
    const t = setTimeout(() => setSwitchToast(''), 2500);
    return () => clearTimeout(t);
  }, [switchToast]);

  useEffect(() => {
    if (window.FB) return;
    window.fbAsyncInit = function() {
      window.FB.init({ appId: FB_APP_ID, autoLogAppEvents: true, xfbml: true, version: 'v21.0' });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true; script.defer = true;
    document.body.appendChild(script);
  }, []);

  const launchAddAccountSignup = () => {
    setAddAccError('');
    if (!window.FB) { setAddAccError('Facebook SDK is still loading. Please wait a moment and try again.'); return; }
    setAddAccLoading(true);
    window.FB.login((response) => {
      (async () => {
        if (response.authResponse?.code) {
          try {
            const res = await api.post('/connect/whatsapp', {
              code: response.authResponse.code,
              is_add_account: true,
              session_info: response.authResponse.session_info || {}
            });
            if (res.data?.success) {
              window.location.reload();
              return;
            } else {
              setAddAccError('Could not add this account.');
            }
          } catch (e) {
            setAddAccError(e.response?.data?.error || 'Could not add this account.');
          }
        } else {
          setAddAccError('Facebook login cancelled or failed.');
        }
        setAddAccLoading(false);
      })();
    }, { config_id: '26070060269330483', response_type: 'code', override_default_response_type: true, scope: 'whatsapp_business_management,whatsapp_business_messaging,public_profile', extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
  };

  useEffect(() => {
    if (!user) return;
    api.get('/businesses?_t=' + Date.now()).then(r => {
      const data = Array.isArray(r.data) ? r.data : [];
      const filtered = user?.role === 'admin' ? data : data.filter(b => b.id === user?.business_id);
      setBusinesses(filtered);
      if (filtered.length > 0 && !selectedBusiness) selectBusiness(filtered[0]);
    }).catch(() => {}).finally(() => setLoadingBiz(false));
  }, [user]);

  const automationSubItems = [
    { to: '/automation', label: 'Basic Automations', icon: '🔔' },
    { to: '/automation/custom-reply', label: 'Custom Auto Reply', icon: '↩️' },
    { to: '/automation/workflows', label: 'Workflows', icon: '🔄' },
    { to: '/automation/ai-intent', label: 'AI Intent Matching', icon: '✨' },
    { to: '/automation/ai-agent', label: 'WhatsApp AI Agent', icon: '🤖' },
  ];

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: '📊' },
    { to: '/inbox', label: 'Inbox', icon: '💬' },
    { to: '/campaigns', label: 'Campaigns', icon: '📣' },
    { to: '/contacts', label: 'Contact List', icon: '👥' },
    { to: '/templates', label: 'Templates', icon: '📝' },
    { to: '/leads', label: 'Leads CRM', icon: '🎯' },
    { to: '/integrations', label: 'Integrations', icon: '🔗' },
    ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin Panel', icon: '🛡️' }] : []),
  ];

  const analyticsNavItems = [
    { to: '/reports', label: 'Reports', icon: '📊' },
    { to: '/analytics', label: 'Analytics', icon: '📉' },
    { to: '/campaign-reports', label: 'Campaign Reports', icon: '📈' },
    { to: '/bulk-campaigns', label: 'Bulk Campaigns', icon: '📢' },
  ];

  const manageNavItems = [
    { to: '/notifications', label: 'Notifications', icon: '🔔' },
    { to: '/whatsapp-forms', label: 'WA Forms', icon: '📋' },
    { to: '/whatsapp-profile', label: 'WhatsApp Profile', icon: '📱' },
    { to: '/tags', label: 'Tags', icon: '🏷️' },
    { to: '/contact-attributes', label: 'Contact Attributes', icon: '🎯' },
    { to: '/message-logs', label: 'Message Logs', icon: '🗂️' },
  ];

  if (loadingBiz) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: 18, color: '#6b7280' }}>Loading...</div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0eeff', fontFamily: 'Poppins, sans-serif' }}>
      <aside style={{
        width: 280, background: '#f5f3ff',
        borderRight: '1px solid #ede9fe',
        display: 'flex', flexDirection: 'column', height: '100vh',
        position: 'sticky', top: 0, zIndex: 950,
        boxShadow: '2px 0 12px rgba(124,58,237,0.06)',
      }}>
        {/* Logo */}
        <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid #ede9fe' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <img src="/logo.png" alt="VBOT" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#4c1d95', letterSpacing: 0.3 }}>VBOT</div>
              <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>WhatsApp CRM</div>
            </div>
          </div>
          {user?.role !== 'admin' && (
            <button onClick={() => navigate('/connect')} style={{
              width: '100%', padding: '9px', borderRadius: 9, border: 'none',
              background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              letterSpacing: 0.3, boxShadow: '0 4px 10px rgba(124,58,237,0.25)',
            }}>⚡ Connect WhatsApp</button>
          )}
        </div>

        {/* Nav */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px', scrollbarWidth: 'none' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 1.5, padding: '6px 8px 4px' }}>Main Menu</div>

          <nav>
            {navItems.map(item => (
              <NavLink key={item.to} to={item.to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 1, borderRadius: 9,
                  textDecoration: 'none', fontSize: 15,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#5b21b6' : '#6b7280',
                  background: isActive ? '#ede9fe' : 'transparent',
                  borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                  transition: 'all 0.15s',
                })}>
                <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Analytics */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 1.5, padding: '10px 8px 4px' }}>Analytics</div>
          <nav>
            {analyticsNavItems.map(item => (
              <NavLink key={item.to} to={item.to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 1, borderRadius: 9,
                  textDecoration: 'none', fontSize: 15,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#5b21b6' : '#6b7280',
                  background: isActive ? '#ede9fe' : 'transparent',
                  borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                  transition: 'all 0.15s',
                })}>
                <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Manage */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 1.5, padding: '10px 8px 4px' }}>Manage</div>
          <nav>
            {manageNavItems.map(item => (
              <NavLink key={item.to} to={item.to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 1, borderRadius: 9,
                  textDecoration: 'none', fontSize: 15,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#5b21b6' : '#6b7280',
                  background: isActive ? '#ede9fe' : 'transparent',
                  borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                  transition: 'all 0.15s',
                })}>
                <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
            <NavLink to="/agents" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 1, borderRadius: 9, textDecoration: 'none', fontSize: 15, fontWeight: isActive ? 600 : 400, color: isActive ? '#5b21b6' : '#6b7280', background: isActive ? '#ede9fe' : 'transparent', borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent', transition: 'all 0.15s' })}>
              <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>👤</span>
              <span>Agents</span>
            </NavLink>
          </nav>
          {/* Automation */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 1.5, padding: '10px 8px 4px' }}>Automation</div>
          <div>
            <div onClick={() => setAutomationOpen(v => !v)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 10px', marginBottom: 1, borderRadius: 9, cursor: 'pointer',
              fontSize: 15, fontWeight: 400, color: '#6b7280',
              borderLeft: '3px solid transparent',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>⚙️</span>
                <span>Automation</span>
              </span>
              <span style={{ fontSize: 11, color: '#a78bfa' }}>{automationOpen ? '▲' : '▼'}</span>
            </div>
            {automationOpen && (
              <div style={{ paddingLeft: 10, borderLeft: '2px solid #ddd6fe', marginLeft: 14, marginBottom: 4 }}>
                {automationSubItems.map(item => (
                  <NavLink key={item.to} to={item.to}
                    style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px', marginBottom: 1, borderRadius: 7,
                      textDecoration: 'none', fontSize: 14,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#7c3aed' : '#9ca3af',
                      background: isActive ? '#ede9fe' : 'transparent',
                    })}>
                    <span style={{ fontSize: 13 }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom - Combined Account Block */}
        <div style={{ padding: '12px 12px', borderTop: '1px solid #ede9fe', position: 'relative' }}>
          {addAccError && (
            <div style={{ background: '#fef2f2', color: '#ef4444', fontSize: 11, padding: '6px 8px', borderRadius: 6, marginBottom: 10 }}>
              {addAccError}
            </div>
          )}

          <div onClick={() => setAccountMenuOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 10, border: '1.5px solid #7c3aed', background: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(124,58,237,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, overflow: 'hidden' }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                {(selectedBusiness?.name || 'B').charAt(0).toUpperCase()}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#4c1d95', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedBusiness?.name || `Business ${selectedBusiness?.id || ''}`}
                </div>
                <div style={{ fontSize: 10, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || user?.email} · {user?.role}
                </div>
              </div>
            </div>
            <span style={{ fontSize: 11, color: '#a78bfa', flexShrink: 0 }}>{accountMenuOpen ? '▲' : '▼'}</span>
          </div>

          {accountMenuOpen && (
            <div style={{ position: 'absolute', bottom: '100%', left: 12, right: 12, marginBottom: 6, background: '#fff', border: '1px solid #ede9fe', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: 8, zIndex: 1000, maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, padding: '2px 8px 6px' }}>Switch Business</div>
              {(user?.role === 'admin' ? businesses : (availableBusinesses || [])).map(b => (
                <div key={b.id} onClick={() => { selectBusiness(b); setSwitchToast(b.name || `Business ${b.id}`); setAccountMenuOpen(false); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 9px', borderRadius: 7, cursor: 'pointer', background: selectedBusiness?.id === b.id ? '#ede9fe' : 'transparent', marginBottom: 2 }}>
                  <span style={{ fontSize: 12, color: selectedBusiness?.id === b.id ? '#5b21b6' : '#374151', fontWeight: selectedBusiness?.id === b.id ? 700 : 400 }}>{b.name || `Business ${b.id}`}</span>
                  {selectedBusiness?.id === b.id && <span style={{ fontSize: 11, color: '#7c3aed' }}>✓</span>}
                </div>
              ))}
              <div style={{ borderTop: '1px solid #f3f4f6', margin: '5px 3px' }} />
              <div onClick={() => { logout(); navigate('/login'); }} style={{ padding: '7px 9px', fontSize: 12, color: '#ef4444', fontWeight: 600, cursor: 'pointer', borderRadius: 7 }}>
                Logout
              </div>
            </div>
          )}
        </div>
      </aside>

      <main style={{ flex: 1, padding: location.pathname === '/inbox' ? '0' : '24px 28px', overflow: location.pathname === '/inbox' ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
        {switchToast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 8, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#065f46', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          Switched to {switchToast}
        </div>
      )}
      <Outlet />
      </main>

    </div>
  );
}
